<template>
    <div id="org_settings_scorecard">
        <div class="d-flex">
            <img src="/img/icons/nav-icons/Scorecard.svg" alt="Scorecard Filter" class="card-icon"></img>
        
            <div class="ms-3 flex-grow-1">
                <div class="d-flex align-items-center justify-content-between">
                    <h3 class="mb-0">Scorecard Filter</h3>
                    <!-- save status indicator -->
                    <!-- <div v-show="user_prioritization.length !== 0">
                        <small v-if="form.busy" class="text-muted">
                            <i class="fa fa-spinner fa-spin me-1"></i> Saving...
                        </small>
                        <small v-else-if="form.lastSaved" class="text-success">
                            <i class="fa fa-check me-1"></i> Saved
                        </small>
                    </div> -->
                </div>
                <p>Filter your Scorecard to show only the metrics that matter to you.</p>
            </div>
        </div>
        
        <div v-show="(!data_blend || !data_blend.id) || !filter_loaded">
            <h4 class="d-flex align-items-center">
                <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                Loading Filter Options
            </h4>
        </div>
        <div v-show="data_blend && data_blend.id && filter_loaded && !scorecard_filter && !show_filter_builder" class="my-5 py-5 text-center">
            <h4 class="text-center">You haven't set any filters.</h4>
            <p>Add a filter to customize your Scorecard.</p>
            <button 
                class="btn btn-primary btn-primary-scorecard" 
                @click="showFilterBuilder"
                v-if="canEdit('scorecard')"
            >
                Add Filter
            </button>

            <div v-else class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                <button class="btn btn-primary btn-lg" :disabled="true">
                     Add Filter
                </button>
            </div>
        </div>
        <div v-if="data_blend && data_blend.id && filter_loaded && (scorecard_filter || show_filter_builder)" class="mb-5">
            <query-builder-simplified 
                :client="client" 
                v-model="scorecard_filter"
                :static_db_columns="data_blend.source_columns" 
                :source="data_blend.data_source"
                :data_source_id="data_blend.id" 
                :if_allow_edit="canEdit('scorecard')"
                ref="filter"
                :key="'query-builder-scorecard'"
                >
            </query-builder-simplified>
        </div>

        <div v-if="data_blend && data_blend.id && filter_loaded && (scorecard_filter || show_filter_builder)">
            <button v-if="canEdit('scorecard')" type="submit" class="btn btn-primary float-end" @click.prevent="update" :disabled="form.busy" >
                <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
            </button>
            <div v-else class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                <button class="btn btn-primary btn-icon-only-md float-end" :disabled="true">
                    <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../../mixins/permissions';
import { useNotifications } from '../../../composables/useNotifications';

export default defineComponent({

    mixins: [permissionsMixin],
    name: 'ScorecardFilter',
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            local_client: {},
            form: {
                busy: false,
                error: false
            },
            scorecard_filter: null,
            data_blend: null,
            show_filter_builder: false,
            filter_loaded: false
        };
    },
    beforeMount() {
        this.local_client = this.client;
        this.getScorecardFilter();
    },
    computed: {
        hasValidFilters() {
            if (!this.scorecard_filter) {
                return false;
            }
            const logicUnits = this.scorecard_filter.logic_units || [];
            // Check if there are any valid logic units (with non-null db_column)
            return logicUnits.some(unit => unit.db_column && unit.db_column.value);
        }
    },
    methods: {
        getScorecardFilter() {
            window.axios.get('/api/scorecard/' + this.client.url + '/filter', [])
                .then(response => {

                    const filter = response.data.filter;
                    // If the filter exists but has no valid logic units, treat it as null
                    if (filter) {
                        const logicUnits = filter.logic_units || [];
                        const hasValidUnits = logicUnits.some(unit => unit.db_column && unit.db_column.value);
                        if (!hasValidUnits) {
                            this.scorecard_filter = null;
                        } else {
                            this.scorecard_filter = filter;
                        }
                    } else {
                        this.scorecard_filter = null;
                    }
                    
                    this.data_blend = response.data.blend;
                    this.filter_loaded = true;
                    
                    // Reset show_filter_builder if no valid filters exist (filters were cleared)
                    if (!this.scorecard_filter) {
                        this.show_filter_builder = false;
                    }
                    
                    // Emit event to parent to indicate if data_blend is available
                    this.$emit('data-blend-loaded', !!this.data_blend);
                });
        },
        update() {
            this.form.busy = true;
            this.local_client.scorecard_filter = this.scorecard_filter;

            window.axios.put('/api/client/' + this.local_client.url, this.local_client)
                .then(response => {
                    this.form.busy = false;
                    this.showNotification({
                        text: 'Scorecard filter was successfully updated!'
                    });
                    // Refresh the filter data to get the updated state from the backend
                    this.getScorecardFilter();
                }).catch(error => {
                    this.form.busy = false;
                    this.showNotification({
                        text: 'Failed to update scorecard filter. Please try again.',
                        type: 'error'
                    });
                });
        },
        showFilterBuilder() {
            this.show_filter_builder = true;
            
            // Initialize a blank filter object so QueryBuilderSimplified can render placeholder fields
            if (!this.scorecard_filter || !this.scorecard_filter.logic_units) {
                this.scorecard_filter = {
                    logic_units: [],
                    filter_logic: '1',
                    filter_ui: 'simple',
                    filter_option: 'custom',
                    logic_units_modification: 0,
                };
            }
            
            // Ensure there's at least one empty logic unit for the user to start with
            if (this.scorecard_filter.logic_units.length === 0) {
                this.scorecard_filter.logic_units.push({
                    db_column: null,
                    data_type: 'string',
                    operator: null,
                    is_static: true,
                    show_options: false,
                    value: [],
                    modification: 0,
                    operator_option_type: 'short',
                    tooltip_title: '',
                    column_removed_error: null
                });
            }
        }
    }
});
</script>
